import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { MusicalWorkViewStore } from "../../store";
import { MusicalWorkView } from "../view/MusicalWorkView";
import { MusicalWorkUpdateStore } from "../../store";
import { MusicalWorkUpdate } from "../update/MusicalWorkUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type MusicalWorkModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const MusicalWorkModalContent: React.FC<MusicalWorkModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new MusicalWorkUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new MusicalWorkViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <MusicalWorkView
            musicalWorkViewStore={storeInfo.store as MusicalWorkViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <MusicalWorkUpdate
            musicalWorkUpdateStore={storeInfo.store as MusicalWorkUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
